import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

export type Leave = {
  _id: string
  user: {
    _id: string
    firstName: string
    lastName: string
  }
  startDate: string
  endDate: string
   totalDays: number
  type: string
  reason: string
  branch: string
  status: "pending" | "approved" | "rejected" | "cancelled"
  approvedBy?: {
    _id: string
    name: string
  }
  createdAt: string
  updatedAt: string
  isDeleted: boolean
}

export type LeaveCreateData = {
  // Optional when applying on behalf of someone (staff / student)
  userId?: string
  session?: string
  startDate: string
  endDate: string
  type: string
  reason: string
  branch?: string
  status: string
  totalDays: number
}

export type LeaveUpdateData = Partial<LeaveCreateData>

export type LeaveApprovalData = {
  status: "approved" | "rejected"
  remarks?: string
}

export type PaginationData = {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

export type LeavesResponse = {
  success: boolean
  data: {
    leaves: Leave[]
    pagination: PaginationData
  }
}

export type LeaveResponse = {
  success: boolean
  data: Leave
  message?: string
  // Optional quota summary returned from submitLeave controller
  quotaSummary?: LeaveQuotaSummary
}

export type ApiResponse = {
  success: boolean
  data?: any
  message?: string
}

const LEAVE_ENDPOINT = {
  LEAVES: "/leaves",
  LEAVE_QUOTAS: "/leave-quotas",
  LEAVE_QUOTA_SUMMARY: "/leave-quotas/summary",
}

const submitLeave = async (data: LeaveCreateData): Promise<LeaveResponse> => {
  const response = await apiClient({
    url: LEAVE_ENDPOINT.LEAVES,
    method: "POST",
    data,
  })
  return response.data
}

const getAllLeaves = async ({
  page = 1,
  limit = 10,
  sortBy,
  sortOrder,
  status,
  type,
  startDate,
  endDate,
  searchText,
  userId,
}: {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: "asc" | "desc"
  status?: string
  type?: string
  startDate?: string
  endDate?: string
  searchText?: string
  // Filter leaves by specific user (staff / student)
  userId?: string
} = {}): Promise<LeavesResponse> => {
  const response = await apiClient({
    url: appendQueryParams(LEAVE_ENDPOINT.LEAVES, {
      page,
      limit,
      sortBy,
      sortOrder,
      status,
      type,
      startDate,
      endDate,
      searchText,
      userId,
    }),
    method: "GET",
  })
  return response.data
}

const getLeave = async (id: string): Promise<LeaveResponse> => {
  const response = await apiClient({
    url: `${LEAVE_ENDPOINT.LEAVES}/${id}`,
    method: "GET",
  })
  return response.data
}

const editLeave = async (id: string, data: LeaveUpdateData): Promise<LeaveResponse> => {
  const response = await apiClient({
    url: `${LEAVE_ENDPOINT.LEAVES}/${id}`,
    method: "PUT",
    data,
  })
  return response.data
}

const removeLeave = async (id: string): Promise<ApiResponse> => {
  const response = await apiClient({
    url: `${LEAVE_ENDPOINT.LEAVES}/${id}`,
    method: "DELETE",
  })
  return response.data
}

const cancelLeave = async (id: string): Promise<LeaveResponse> => {
  const response = await apiClient({
    url: `${LEAVE_ENDPOINT.LEAVES}/cancel/${id}`,
    method: "PUT",
  })
  return response.data
}

const approveOrRejectLeave = async (id: string, data: LeaveApprovalData): Promise<LeaveResponse> => {
  const response = await apiClient({
      url: `${LEAVE_ENDPOINT.LEAVES}/approve-reject/${id}`,
    method: "PUT",
    data,
  })
  return response.data
}

export type LeaveQuotaSummary = {
  totalLeaves: number
  takenLeaves: number
  balanceLeaves: number
}

export type LeaveQuota = {
  _id: string
  title?: string
  name?: string
  type?: string | { _id?: string; name?: string; title?: string }
  leaveType?: string | { _id?: string; name?: string; title?: string }
}

const getAllLeaveQuotas = async (params: {
  session?: string
  branch?: string
} = {}): Promise<{ success: boolean; data?: LeaveQuota[] | any }> => {
  const response = await apiClient({
    url: appendQueryParams(LEAVE_ENDPOINT.LEAVE_QUOTAS, {
      ...(params.session && { session: params.session }),
      ...(params.branch && { branch: params.branch }),
    }),
    method: "GET",
  })
  return response.data
}

const getLeaveQuotaSummary = async (params: {
  userId: string
  type: string
  session?: string
  startDate?: string
  branch?: string
}): Promise<{ success: boolean; data?: LeaveQuotaSummary }> => {
  const response = await apiClient({
    url: appendQueryParams(LEAVE_ENDPOINT.LEAVE_QUOTA_SUMMARY, {
      userId: params.userId,
      type: params.type,
      ...(params.session && { session: params.session }),
      ...(params.startDate && { startDate: params.startDate }),
      ...(params.branch && { branch: params.branch }),
    }),
    method: "GET",
  })
  return response.data
}

export {
  submitLeave,
  getAllLeaves,
  getLeave,
  editLeave,
  removeLeave,
  cancelLeave,
  approveOrRejectLeave,
  getAllLeaveQuotas,
  getLeaveQuotaSummary,
}